import axios from "axios";
import { useEffect, useState } from "react";
import { statusIsGood } from "../../../helpers/http";
import { Tooltip } from "@mui/material";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';

export default function GradHeadReview({ review, currentYear, sarID, advisorReviews }) {
    const [gphReview, setGPHReview] = useState({...review});
    const [validating, setValidating] = useState(false);
    const [errors, setErrors] = useState({});
    const [template, setTemplate] = useState(-1);

    const templates = [
        /* Default */ `<p>The Graduate Office concurs with your advisor's evaluation and is pleased to see the steady advancement you are making in your graduate studies. Wishing you continued success in pursuing your academic and professional aspirations.</p>`,
        /* MS No Advisor */ `<p>At this time, you do not have a research advisor on record. Identifying an advisor is an important milestone in the MS program and is necessary to make timely progress toward your degree.</p><p><br></p><p>We encourage you to prioritize this in the coming semester and take active steps to identify and secure an advisor whose research aligns with your interests.</p><p><br></p><p>To support your progress, you should:</p><ul><li><strong>Actively explore faculty research areas</strong> and identify potential matches</li><li><strong>Attend lab meetings, seminars, and departmental talks</strong> to learn more about ongoing research</li><li><strong>Reach out to faculty</strong> to express interest and request meetings</li><li><strong>Follow up and engage consistently</strong> as you build potential advising relationships</li></ul><p><br></p><p>You are also encouraged to connect with Sara Coulson to learn more about a <strong>pilot program launching this Fall</strong> for MS students who have not yet finalized an advisor. This program is designed to provide structure, support your transition into research, and create opportunities to connect with faculty.</p><p><br></p><p>If you would like help identifying potential advisors or thinking through next steps, please reach out. We are here to support you in making progress toward this milestone.</p>`,
        /* PhD No Advisor [2012 Characters...] */ `<p>At this stage, you do not have a research advisor on record. Securing an advisor is an important milestone in the PhD program, and we want to support you in finding a strong fit that aligns with your interests and goals.</p><p><br></p><p>You are expected to secure a research advisor before the start of the Spring 2027 semester. If an advisor is not in place by that time, you would not be able to continue in the PhD program. Our goal is to help you make meaningful progress toward this milestone over the coming months.</p><p><br></p><p>To support you in this process, we encourage you to:</p><ul><li><strong>Connect with faculty</strong> whose research aligns with your interests</li><li><strong>Attend departmental talks, seminars, and lab meetings</strong> to explore different research areas and advising styles</li><li><strong>Engage in programs and events</strong> designed to help students identify potential advisors</li><li><strong>Be proactive</strong> in starting conversations and following up with faculty you are interested in working with</li></ul><p><br></p><p>At the same time, we want to make sure you are in a strong position regardless of how your plans evolve. As a parallel path, we recommend that you meet with your Graduate Coordinator this summer (Sharon Kinder in Blacksburg or Klaudia Escobar in Alexandria) to:</p><ul><li>Finalize your Fall 2026 schedule so that you meet the Master’s Along the Way degree requirements: <a href="https://students.cs.vt.edu/Graduate/MSCoursework.html" rel="noopener noreferrer" target="_blank">https://students.cs.vt.edu/Graduate/MSCoursework.html</a></li><li>Complete the necessary paperwork to secure your Master’s Along the Way</li></ul><p><br></p><p>The Master’s Along the Way is a valuable milestone that ensures you have a completed graduate credential and provides flexibility if your goals or circumstances change. To stay on track for Fall 2026 conferral, this paperwork must be completed and ready by the start of the Fall semester.</p>`,
        /* PHD 5+ Years */`<p>You are currently beyond the department’s 5-year expectation for completion of the PhD program. We recognize that doctoral timelines can vary, and we encourage you to work closely with your advisor to establish a clear and realistic plan for timely degree completion.</p><p><br></p><p>As you move beyond this timeframe, it is important to be aware of the following funding considerations:</p><ul><li>The department cannot guarantee funding for students continuing beyond the 5-year PhD timeline</li><li>You will be considered in Round 2 of the GTA assignment process rather than receiving priority placement</li><li>In Round 2, GTA positions are not guaranteed and are offered based on instructional needs within the department and alignment between your skills and course staffing needs</li></ul><p>This means that GTA support may be limited or intermittent, and you should plan accordingly.</p><p><br></p><p>If you have external funding, we encourage you to use this time intentionally by working with your advisor to develop a clear, completion-focused plan with defined milestones and a realistic timeline for finishing your degree.</p><p><br></p><p>All students should align their plans with the Graduate School’s guidance on degree completion, including the steps outlined here:</p><ul><li><a href="https://graduateschool.vt.edu/academics/what-you-need-to-graduate.html" rel="noopener noreferrer" target="_blank"> https://graduateschool.vt.edu/academics/what-you-need-to-graduate.html </a></li></ul><p><br></p><p>To support your progress, we encourage you to:</p><ul><li>Work with your advisor to define a clear timeline and milestones for completion</li><li>Prioritize dissertation research and writing</li><li>Explore funding options with your advisor, including potential GRA support</li><li>Review Graduate School requirements and deadlines to ensure you are on track for graduation</li></ul>`,
    ]

    function handleChange(event) {
        const {name, value} = event.target;
        let v = Array.isArray(value) || isNaN(value) || value === "" ? value : parseInt(value);
        let [, field] = name.split(".");
        setGPHReview({...gphReview, [field]: v});
    }

    function validateReview() {
        let newErrors = {};
        //Student Progress Value
        if(gphReview.progressValue === "" || gphReview.progressValue === null || gphReview.progressValue === undefined)
            newErrors.progressValue = `Must give a rating`;
        //Student Progress Text
        if(gphReview.descriptiveEval === "" || gphReview.descriptiveEval.trim() === '<p><br></p>')
            newErrors.descriptiveEval = `Cannot be blank`;
        else if(gphReview.descriptiveEval.length > 3000)
            newErrors.descriptiveEval = "Answer must be 3000 characters or less"
        setErrors(newErrors);
        return newErrors;
    }
    
    function checkLengths() {
        let newErrors = {};
        if(gphReview.descriptiveEval.length > 3000)
            newErrors.descriptiveEval = "Answer must be 3000 characters or less"
        setErrors(newErrors);
        return newErrors;
    }

    function onSave(isSubmit = false) {
        if(isSubmit) {
            setValidating(true);
            let validationErrors = validateReview();
            if(Object.keys(validationErrors).length > 0) {
                alert("Please fix errors before submitting");
                return;
            }
            if(!window.confirm("Once shared you will not be able to make further changes, are you sure you would like to share your review with the student?"))
                return;
        } else {
            let lengthErrors = checkLengths();
            if(Object.keys(lengthErrors).length > 0) {
                alert("Please fix errors before saving");
                return;
            }
        }
        let cleanedReview = {...gphReview};
        Object.keys(cleanedReview).forEach((key) => {
            if(typeof cleanedReview[key] === "string" && (cleanedReview[key].trim() === "" || cleanedReview[key].trim() === '<p><br></p>')) {
                cleanedReview[key] = "";
            }
        });
        axios.post(`/api/sar_evals/${sarID}/gph`,
            {...cleanedReview, reviewStatus: isSubmit ? 2 : 1}
        ).then((response) => {
            if(statusIsGood(response.status)) {
                alert(isSubmit ? "Review shared successfully" : "Review saved successfully");
                if(isSubmit) window.location.reload();
            } else {
                console.error(response.errors);
                alert("Faled to save review");
            }
        }).catch((error) => {
            if(Object.hasOwn(error.response, 'data')) {
                console.error(error.response);
                if(error.response.status === 401) {
                    alert("An error has occured with your connection and you have been logged out. "+
                        "To keep your unsaved work, please open the site in a new tab/window, log in "+
                        "(depending on how you reopen the site you may need to refresh the new tab/window to be prompted)"+
                        ", and then try resubmitting. Contact an admin if this persists.");
                } else {
                    alert(error.response.data.message)
                }
            } else {
                alert("Error saving Review, \nContact an admin if this persists.");
                console.error(error.response);
            }
        });
    }

    useEffect(() => {
        if(template === -1) return;
        let progressValue = 3;
        if(template === 0) {
            progressValue = Math.min(...advisorReviews.map(ar => ar.progressValue));
        }
        setGPHReview({
            ...gphReview,
            progressValue,
            descriptiveEval: templates[template]
        });
    }, [template]);

    useEffect(() => {
        if(validating) validateReview();
        else checkLengths();
    }, [gphReview, validating]);

    useEffect(() => {
        if(gphReview.reviewStatus === 0)
            setTemplate(0);
    }, [])

    return(
        <fieldset>
            {gphReview.reviewStatus !== 2 && <legend style={{color: "red"}}> (DRAFT)</legend>}
            {gphReview?.progressValue !== undefined ?
            <ol>
                <li>
                    <p>How would you define the student's progress?{gphReview.reviewStatus !== 2 && <Tooltip title="Required"><strong>*</strong></Tooltip>}</p>
                    <div className="ea-options-div" style={{display:"flex", flexWrap:"wrap"}}>
                        <div className="ea-radios">
                            <input
                                type="radio"
                                id={`gphReview.progressValue-Very_Good_Progress`}
                                name={`gphReview.progressValue`}
                                value={1}
                                checked={gphReview.progressValue === 1}
                                onChange={(e) => {if(gphReview.reviewStatus === 2){} else handleChange(e)}}
                            />
                            <label
                                className="form-radio-options"
                                htmlFor={`gphReview.progressValue-Very_Good_Progress`}
                            >Very Good Progress</label>
                        </div>
                        <div className="ea-radios">
                            <input
                                type="radio"
                                id={`gphReview.progressValue-Satisfactory`}
                                name={`gphReview.progressValue`}
                                value={2}
                                checked={gphReview.progressValue === 2}
                                onChange={(e) => {if(gphReview.reviewStatus === 2){} else handleChange(e)}}
                            />
                            <label
                                className="form-radio-options"
                                htmlFor={`gphReview.progressValue-Satisfactory`}
                            >Satisfactory</label>
                        </div>
                        <div className="ea-radios">
                            <input
                                type="radio"
                                id={`gphReview.progressValue-Unsatisfactory`}
                                name={`gphReview.progressValue`}
                                value={3}
                                checked={gphReview.progressValue === 3}
                                onChange={(e) => {if(gphReview.reviewStatus === 2){} else handleChange(e)}}
                            />
                            <label
                                className="form-radio-options"
                                htmlFor={`gphReview.progressValue-Unsatisfactory`}
                            >Unsatisfactory</label>
                        </div>
                        <div className="ea-radios">
                            <input
                                type="radio"
                                id={`gphReview.progressValue-Lack_of_Minimal_Progress`}
                                name={`gphReview.progressValue`}
                                value={4}
                                checked={gphReview.progressValue === 4}
                                onChange={(e) => {if(gphReview.reviewStatus === 2){} else handleChange(e)}}
                            />
                            <label
                                className="form-radio-options"
                                htmlFor={`gphReview.progressValue-Lack_of_Minimal_Progress`}
                            >Lack of Minimal Progress</label>
                        </div>
                    </div>
                    <span className="feedback">{errors.progressValue}</span>
                </li>
                <li>
                    <p>Please describe the student's progress{gphReview.reviewStatus !== 2 && <Tooltip title="Required"><strong>*</strong></Tooltip>}</p>
                    {(!!template || template === 0) &&
                        <ReactQuill
                            theme="snow"
                            value={gphReview.descriptiveEval}
                            onChange={(value) => {
                                if(gphReview.reviewStatus === 2) {}
                                else if(gphReview.descriptiveEval !== value) {
                                    handleChange({target:{
                                        name: `gphReview.descriptiveEval`,
                                        type: "text",
                                        value,
                                        checked: false,
                                    }})
                                }
                                    
                            }}
                            modules={{toolbar: gphReview.reviewStatus === 2 ? false : [
                                ['bold', 'italic', 'underline'], 
                                [{ list: 'ordered' }, { list: 'bullet' }], 
                                ['link'], 
                            ],}}
                            readOnly={gphReview.reviewStatus === 2}
                            placeholder={gphReview.reviewStatus === 2 ?
                                "No descriptive evaluation provided." :
                                "Use this space to provide describe the student's progress"
                            }
                        />
                    }
                    {gphReview.reviewStatus !== 2 && 
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <span className="feedback">{errors.descriptiveEval}</span>
                        <Tooltip title="Styling takes up extra characters">
                            <span style={{
                                color: gphReview.descriptiveEval?.length > 3000 && "red",
                                fontWeight: gphReview.descriptiveEval?.length >= 2900 && "bold"
                            }}>{gphReview.descriptiveEval?.length}/3000*</span>
                        </Tooltip>
                    </div>}
                </li>
            </ol>:<></>}
            {gphReview.reviewStatus !== 2 &&
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline"}}>
                <div style={{display:"flex", alignItems:"baseline"}}>
                    <label
                        className="form-label"
                        htmlFor="gph-template"
                        style={{marginLeft: "1em", marginRight: "0.5em"}}
                    >Template:</label>
                    <select
                        id="gph-template"
                        name="gph-template"
                        className="form-select"
                        value={template}
                        onChange={(e) => setTemplate(parseInt(e.target.value))}
                    >
                        <option value={-1}></option>
                        <option value={0}>Default</option>
                        <option value={1}>MS No Advisor</option>
                        <option value={2}>PhD No Advisor</option>
                        <option value={3}>PHD 5+ Years</option>
                    </select>
                </div>
                <div style={{display:"flex", alignItems:"baseline"}}>
                    <div className="form-check">
                        <input
                            id="validation-checkbox"
                            name="validation-checkbox"
                            type="checkbox"
                            className="form-check-input"
                            checked={validating}
                            onChange={(e) => setValidating(e.target.checked)}
                        />
                        <label
                            htmlFor="validation-checkbox"
                            className="form-check-label"
                            style={{paddingRight: "10px"}}
                        >Validating?</label>
                    </div>
                    <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => onSave(false)}
                    >Save</button>
                    <button
                        type="button"
                        className="btn btn-warning"
                        onClick={() => onSave(true)}
                    >Share</button>
                </div>
            </div>
            }
        </fieldset>
    )
}